import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import type { UserProfileData } from 'api-client';
import { useAppContext } from '@/context';
import ProfileEditorFields from './ProfileEditorFields';
import TwoFactorSection from './TwoFactorSection';
import SocialAccountsSection from './SocialAccountsSection';
import APIKeysSection from './APIKeysSection';
import { profileFailure, useProfileResource } from './profileRequests';
import { NativeAccountPanel } from './NativeAccountPage';

function ProfileSettings() {
  const { apiClients } = useAppContext();
  const load = useCallback(() => apiClients.users.usersProfileRetrieve(), [apiClients.users]);
  const resource = useProfileResource(load, 'Could not load your profile. Please retry.');
  const [draft, setDraft] = useState<UserProfileData | null>(null);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const user = resource.data?.user;
  const id = user?.id;
  const email = user?.email;
  const firstName = user?.firstName || '';
  const lastName = user?.lastName || '';
  const language = user?.language || '';
  const timezone = user?.timezone || '';
  useEffect(() => {
    if (email !== undefined) setDraft({ firstName, lastName, email, language, timezone });
    // A confirmed picture change must not discard unsaved profile fields.
  }, [id, firstName, lastName, email, language, timezone]);
  const action = async (run: () => Promise<void>) => {
    if (pending) return;
    const current = resource.generation.current;
    setPending(true);
    setError(null);
    setSuccess(null);
    try {
      await run();
    } catch (failure) {
      const message = await profileFailure(failure, 'Could not save your profile. Please retry.');
      if (current === resource.generation.current) setError(message);
    } finally {
      if (current === resource.generation.current) setPending(false);
    }
  };
  const save = (event: FormEvent) => {
    event.preventDefault();
    if (!draft || !resource.data) return;
    const current = resource.generation.current;
    void action(async () => {
      const result = await apiClients.users.usersProfileUpdate({ profileUpdateRequest: draft });
      if (current !== resource.generation.current) return;
      resource.setData((data) =>
        data ? { ...data, pendingEmail: result.pendingEmail, user: { ...data.user, ...result.user } } : data,
      );
      setDraft(result.user);
      setSuccess(result.message);
      try {
        const refreshed = await apiClients.users.usersProfileRetrieve();
        if (current === resource.generation.current) resource.setData(refreshed);
      } catch {
        if (current === resource.generation.current)
          setError('Profile saved, but current verification status could not be refreshed. Reload to refresh.');
      }
    });
  };
  const avatar = (file: File) => {
    const current = resource.generation.current;
    void action(async () => {
      const result = await apiClients.users.usersProfileAvatarUpdate({ avatar: file });
      if (current !== resource.generation.current) return;
      resource.setData((data) =>
        data ? { ...data, user: { ...data.user, avatarUrl: result.avatarUrl || data.user.avatarUrl } } : data,
      );
      setSuccess(result.message);
    });
  };
  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1">
          My Profile
        </Typography>
        <Card>
          <CardContent>
            {resource.loading && (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress aria-label="Loading profile" />
                <Typography>Loading profile…</Typography>
              </Stack>
            )}
            {resource.error && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" onClick={resource.retry}>
                    Retry
                  </Button>
                }
              >
                {resource.error}
              </Alert>
            )}
            {resource.data && draft && (
              <ProfileEditorFields
                value={draft}
                languages={resource.data.languages}
                timezones={resource.data.timezones}
                avatarUrl={resource.data.user.avatarUrl}
                confirmedEmail={resource.data.user.email}
                emailVerified={resource.data.emailVerified}
                pendingEmail={resource.data.pendingEmail}
                pending={pending}
                error={error}
                success={success}
                onChange={setDraft}
                onSubmit={save}
                onAvatar={avatar}
              />
            )}
          </CardContent>
        </Card>
        <TwoFactorSection />
        <SocialAccountsSection />
        <APIKeysSection />
      </Stack>
    </Box>
  );
}

export default function ProfileForm() {
  const { user } = useAppContext();
  return user ? (
    <ProfileSettings />
  ) : (
    <NativeAccountPanel
      title="Sign In to Manage Your Profile"
      description="Sign in to view and update your account settings."
      label="Sign In"
      href="/accounts/login/?next=%2Fauth%2Fprofile"
      returnHref="/"
      returnLabel="Return Home"
    />
  );
}
